import React, { useCallback, useMemo, useState } from 'react';
import {
  AutoComplete,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import { Rule } from 'antd/es/form';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import classNames from 'classnames';
import groupBy from 'lodash/groupBy';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
  AggregateFunction,
  AggregatePeriod,
  CurrencyFragment,
  PlanComponentType,
  RedeemType,
  RewardFragment,
} from '@/graphql/generated/graphql';
import { formatNumberToLocale } from '@/utils';
import { ConnectorsParameters } from '@/pages/Plan/PlanModal';
import { AggregateFunctionList, AggregatePeriodList } from '@/constants/PlanOptions';
import { CurrencyInput, If } from '@/components';
import ConnectorTitle from './ConnectorTitle';
import styles from './styles.module.pcss';
import dayjs from 'dayjs';
import CronPicker from '../CronPicker';
import { isCronValid } from '@/utils/isCronValid';
import { useReactiveVar } from '@apollo/client';
import { currentPlanVar } from '@/graphql/operations/plan';

const { Item } = Form;
const { Option, OptGroup } = Select;

export type RenderOptionT = {
  step: 'When' | 'If' | 'Then';
  position: 'LHS' | 'RHS';
};

type Name =
  | 'aggregateFunction'
  | 'conditionParameterId'
  | 'parameterRewardOrFlatValue'
  | 'aggregatePeriod'
  | 'multiplier'
  | 'rewardIncentiveValue'
  | 'customAggregationStartPeriod'
  | 'customAggregationEndPeriod'
  | 'customAggregationStartCronString'
  | 'customAggregationEndCronString';

type InputNames = Partial<Record<Name, any>>;
type InitialValues = Partial<Record<Name, string | number | null | undefined>>;

type Props = {
  renderOption: RenderOptionT;
  parameters?: ConnectorsParameters[];
  names: InputNames;
  onSelectParameterId?: (value: any, options: any) => void;
  onChangeParameterValue?: (value: string, options: any) => void;
  onSelectAggregateFunction?: (value: any, option: any) => void;
  onSelectRewardId?: (value: any, option: any) => void;
  onToggleRewardsSelectInput?: (e: CheckboxChangeEvent) => void;
  aggregateFunction?: AggregateFunction | string | null;
  initialValues?: InitialValues;
  rewardOptions?: Array<
    {
      value: string;
      label: string;
    } & RewardFragment
  >;
  redeemType?: RedeemType | null;
  size?: SizeType;
  rules?: Rule[];
  inputType?: 'parameter' | 'reward' | null;
  componentType?: PlanComponentType | null;
  currency?: CurrencyFragment;
  filterCondition?: boolean;
};

const GenericPlanBuilderInput: React.FC<Props> = ({
  parameters,
  onSelectParameterId,
  onChangeParameterValue,
  onSelectRewardId,
  onToggleRewardsSelectInput,
  names,
  size = 'large',
  onSelectAggregateFunction,
  rules = [{ required: true, message: 'Required' }],
  renderOption,
  aggregateFunction,
  initialValues,
  rewardOptions,
  redeemType,
  componentType,
  inputType,
  currency,
  filterCondition = false,
}) => {
  const currentPlan = useReactiveVar(currentPlanVar);
  const isWhenLHS = renderOption.step === 'When' && renderOption.position === 'LHS';
  const isWhenRHS = renderOption.step === 'When' && renderOption.position === 'RHS';
  const isIfLHS = renderOption.step === 'If' && renderOption.position === 'LHS';
  const isIfRHS = renderOption.step === 'If' && renderOption.position === 'RHS';
  const isThenRHS = renderOption.step === 'Then' && renderOption.position === 'RHS';
  const [isCustom, setIsCustom] = useState<boolean>(
    !!(initialValues && initialValues.aggregatePeriod === AggregatePeriod.Custom)
  );
  const [isSpecificDate, setIsSpecificDate] = useState<boolean>(
    !!(
      initialValues &&
      initialValues.customAggregationStartPeriod &&
      !isCronValid(initialValues.customAggregationStartPeriod as string)
    )
  );
  const form = Form.useFormInstance<InitialValues>();

  const handleSwitchChange = (value: boolean) => {
    setIsSpecificDate(value);
    if (
      names.customAggregationStartCronString &&
      form.getFieldValue(names.customAggregationStartCronString) === undefined
    ) {
      form.setFieldValue(names.customAggregationStartCronString, undefined);
    }
    if (
      names.customAggregationEndCronString &&
      form.getFieldValue(names.customAggregationEndCronString) === undefined
    ) {
      form.setFieldValue(names.customAggregationEndCronString, undefined);
    }
  };

  const defineRenderCondition = (name: string): boolean => {
    switch (name) {
      case 'multiplier':
        return inputType === 'parameter' && renderOption.position === 'RHS';
      case 'parameterRewardOrFlatValue':
        return renderOption.position === 'RHS';
      case 'conditionParameterId':
        return renderOption.position === 'LHS';
      case 'aggregateFunction':
        return renderOption.position === 'LHS' || isThenRHS;
      case 'aggregatePeriod':
        return (renderOption.position === 'LHS' || isThenRHS) && aggregateFunction !== AggregateFunction.None;
      case 'rewardIncentiveValue':
        return inputType === 'reward';
      default:
        return false;
    }
  };

  const getRewardOptionLabel = useCallback((reward: RewardFragment) => {
    if (reward.isCustom) {
      if (reward.redeemType === RedeemType.Points) {
        return `${reward.name} - Min. ${formatNumberToLocale({
          number: reward.minAmount,
          style: 'currency',
          currencyMinimumFractionDigits: 0,
          currencyMaximumFractionDigits: 2,
        })}`;
      } else {
        return `${reward.name} - ${formatNumberToLocale({
          number: reward.rewardAmount,
          style: 'currency',
          currencyMinimumFractionDigits: 0,
          currencyMaximumFractionDigits: 2,
        })}`;
      }
    } else {
      if (reward.redeemType === RedeemType.Points) {
        return `${reward.name} - Min. ${formatNumberToLocale({
          number: reward.minAmount,
          style: 'currency',
          currencyMinimumFractionDigits: 0,
          currencyMaximumFractionDigits: 2,
        })}`;
      } else {
        return `${reward.name} - ${formatNumberToLocale({
          number: reward.rewardAmount,
          style: 'currency',
          currencyMinimumFractionDigits: 0,
          currencyMaximumFractionDigits: 2,
        })}`;
      }
    }
  }, []);

  const autoCompleteOptions = useMemo(() => {
    if (inputType === 'reward') {
      const rewardsByType = groupBy(rewardOptions, 'rewardType');
      return Object.keys(rewardsByType).map((key) => {
        return {
          value: key,
          label: <Typography.Text>{key}</Typography.Text>,
          searchvalue: key,
          options: rewardsByType[key].map((reward) => ({
            value: reward.value,
            label: getRewardOptionLabel(reward),
            searchvalue: getRewardOptionLabel(reward),
            iscustom: reward.isCustom,
            redeemtype: reward.redeemType,
            incentivereward: reward.incentiveReward,
            disabled: false,
          })),
        };
      });
    } else {
      return parameters?.map((connector) => ({
        value: connector.id,
        label: (
          <ConnectorTitle
            title={connector.name}
            connectorData={connector.connectorData}
            archived={connector.archived}
          />
        ),
        searchvalue: connector.name,
        options: connector.parameters.map((parameter) => ({
          value: parameter.id,
          label: (
            <Typography.Text type={connector.archived ? 'danger' : undefined}>{parameter.key}</Typography.Text>
          ) as any,
          disabled: connector.archived,
          searchvalue: parameter.key,
        })),
      }));
    }
  }, [getRewardOptionLabel, inputType, parameters, rewardOptions]);

  const setResponsiveColOptions = (colName: string) => {
    if (isWhenLHS || isIfLHS) {
      if (colName === 'aggregateFunction') {
        return {
          xl: 6,
          xxl: 7,
        };
      } else if (colName === 'aggregatePeriod') {
        return {
          xl: 8,
          xxl: 8,
        };
      } else if (colName === 'conditionParameterId') {
        if (aggregateFunction !== AggregateFunction.None) {
          return {
            xl: 10,
            xxl: 9,
          };
        } else {
          return {
            xl: 18,
            xxl: 17,
          };
        }
      }
    } else if (isWhenRHS || isIfRHS) {
      if (colName === 'multiplier') {
        return {
          xl: 7,
          xxl: 6,
        };
      } else if (colName === 'parameterRewardOrFlatValue') {
        if (inputType === 'parameter') {
          return {
            xl: 17,
            xxl: 18,
          };
        } else {
          return {
            xl: 24,
            xxl: 24,
          };
        }
      }
    } else if (isThenRHS) {
      if (colName === 'aggregateFunction') {
        return {
          xl: 5,
          xxl: 5,
        };
      } else if (colName === 'aggregatePeriod') {
        return {
          xl: 6,
          xxl: 5,
        };
      } else if (colName === 'parameterRewardOrFlatValue') {
        if (aggregateFunction !== AggregateFunction.None) {
          return {
            xl: 9,
            xxl: 10,
          };
        } else if (inputType === 'parameter' || inputType === 'reward') {
          return {
            xl: 15,
            xxl: 15,
          };
        } else {
          return {
            xl: 19,
            xxl: 19,
          };
        }
      } else if (colName === 'multiplier' || colName === 'rewardIncentiveValue') {
        return {
          xl: 4,
          xxl: 4,
        };
      }
    }
  };

  const withRewardsChange = (e: CheckboxChangeEvent) => {
    onToggleRewardsSelectInput?.(e);
  };

  return (
    <div className={styles.root}>
      <Row align="top">
        <If condition={defineRenderCondition('aggregateFunction')}>
          <Col {...setResponsiveColOptions('aggregateFunction')} className={styles.aggregateFunction}>
            <Item name={names.aggregateFunction} initialValue={initialValues?.aggregateFunction} rules={rules}>
              <Select
                onSelect={onSelectAggregateFunction}
                placeholder="Select aggregator"
                size={size}
                disabled={!currentPlan.metadata.isEditing || filterCondition}
                suffixIcon={!currentPlan.metadata.isEditing || !filterCondition}
              >
                {AggregateFunctionList.map((data) => (
                  <Option key={data.value} value={data.value}>
                    <Typography.Text>{data.title}</Typography.Text>
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
        </If>
        <If condition={defineRenderCondition('conditionParameterId')}>
          <Col {...setResponsiveColOptions('conditionParameterId')} className={styles.parameter}>
            <Item name={names.conditionParameterId} initialValue={initialValues?.conditionParameterId} rules={rules}>
              <Select
                size={size}
                showSearch
                disabled={!currentPlan.metadata.isEditing}
                optionLabelProp="label"
                placeholder={
                  <Space>
                    <Typography.Text style={{ color: 'var(--arya-primary-color)' }}>of</Typography.Text>
                    Select Parameter
                  </Space>
                }
                filterOption={(inputValue, option) =>
                  option?.customvalue
                    ? option.customvalue.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    : false
                }
                suffixIcon={!currentPlan.metadata.isEditing}
              >
                {parameters?.map((connector) => (
                  <OptGroup
                    key={connector.id}
                    label={
                      <ConnectorTitle
                        title={connector.name}
                        connectorData={connector.connectorData}
                        archived={connector.archived}
                      />
                    }
                  >
                    {connector.parameters.map((parameter) => (
                      <Option
                        label={
                          <Space>
                            <Typography.Text style={{ color: 'var(--arya-primary-color)' }}>of</Typography.Text>
                            <Typography.Text type={connector.archived ? 'danger' : undefined}>
                              {parameter.key}
                            </Typography.Text>
                          </Space>
                        }
                        key={parameter.id}
                        value={parameter.id}
                        customvalue={parameter.key}
                        disabled={connector.archived}
                      >
                        <Typography.Text type={connector.archived ? 'danger' : undefined}>
                          {parameter.key}
                        </Typography.Text>
                      </Option>
                    ))}
                  </OptGroup>
                ))}
              </Select>
            </Item>
          </Col>
        </If>
        <If condition={defineRenderCondition('parameterRewardOrFlatValue')}>
          <Col
            {...setResponsiveColOptions('parameterRewardOrFlatValue')}
            className={classNames(styles.parameterRewardOrFlatValue, {
              [styles.hasMultiplier]: inputType === 'parameter',
              [styles.withRewards]: inputType === 'reward',
            })}
          >
            <Item
              name={names.parameterRewardOrFlatValue}
              initialValue={initialValues?.parameterRewardOrFlatValue}
              rules={rules}
            >
              <AutoComplete
                allowClear
                onChange={onChangeParameterValue}
                onSelect={inputType === 'reward' ? onSelectRewardId : onSelectParameterId}
                options={autoCompleteOptions}
                disabled={!currentPlan.metadata.isEditing}
                filterOption={(input, option) =>
                  option?.searchvalue
                    ? option?.searchvalue?.toLowerCase().indexOf(input.toString().toLowerCase()) >= 0
                    : false
                }
                popupRender={(menu) => {
                  if (
                    aggregateFunction === AggregateFunction.None &&
                    isThenRHS &&
                    componentType === PlanComponentType.Plain
                  ) {
                    return (
                      <>
                        {React.cloneElement(menu)}
                        <Divider />
                        <Row gutter={16} style={{ margin: '12px 0' }}>
                          <Col>
                            <Radio.Group
                              options={[
                                { label: 'Parameters', value: 'parameter' },
                                { label: 'Rewards', value: 'reward' },
                              ]}
                              value={inputType}
                              size="small"
                              onChange={withRewardsChange}
                              optionType="button"
                              buttonStyle="solid"
                            />
                          </Col>
                        </Row>
                      </>
                    );
                  }
                  return React.cloneElement(menu);
                }}
                notFoundContent={
                  <div>
                    <Typography.Paragraph strong>
                      {inputType === 'reward'
                        ? 'Reward not Found'
                        : inputType === 'parameter'
                        ? 'Parameter not found'
                        : null}
                    </Typography.Paragraph>
                    <If condition={inputType === null}>
                      <Typography.Paragraph>Enter any Custom Flat Amount.</Typography.Paragraph>
                    </If>
                  </div>
                }
              >
                <Input
                  prefix={
                    isThenRHS ? (
                      <Typography.Text style={{ color: 'var(--arya-primary-color)' }}>of</Typography.Text>
                    ) : null
                  }
                  size={size}
                  placeholder="Select Parameter or Enter Flat Amount"
                  className={styles.autoCompleteInputValue}
                />
              </AutoComplete>
            </Item>
          </Col>
        </If>
        <If condition={defineRenderCondition('aggregatePeriod')}>
          <Col {...setResponsiveColOptions('aggregatePeriod')} className={styles.aggregatePeriod}>
            <Item name={names.aggregatePeriod} initialValue={initialValues?.aggregatePeriod} rules={rules}>
              <Select
                optionLabelProp="label"
                disabled={!currentPlan.metadata.isEditing}
                placeholder={
                  <Space>
                    <Typography.Text style={{ color: 'var(--arya-primary-color)' }}>over</Typography.Text>
                    Select Aggregate Period
                  </Space>
                }
                size={size}
                onChange={(value) => {
                  if (value === AggregatePeriod.Custom) {
                    setIsCustom(true);
                  } else {
                    setIsCustom(false);
                  }
                }}
              >
                {AggregatePeriodList.map((data) => (
                  <Option
                    label={
                      <Space>
                        <Typography.Text style={{ color: 'var(--arya-primary-color)' }}>over</Typography.Text>
                        <Typography.Text>{data.title}</Typography.Text>
                      </Space>
                    }
                    key={data.value}
                    value={data.value}
                  >
                    <Typography.Text>{data.title}</Typography.Text>
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
        </If>
        <If condition={defineRenderCondition('multiplier')}>
          <Col {...setResponsiveColOptions('multiplier')}>
            <Item name={names.multiplier} rules={rules} initialValue={initialValues?.multiplier}>
              <Input
                size={size}
                prefix={<Typography.Text style={{ color: 'var(--arya-primary-color)' }}>&times;</Typography.Text>}
                suffix="%"
                disabled={!currentPlan.metadata.isEditing}
                className={styles.multiplier}
              />
            </Item>
          </Col>
        </If>
        <If condition={defineRenderCondition('rewardIncentiveValue')}>
          <Col {...setResponsiveColOptions('rewardIncentiveValue')}>
            <Item name={names.rewardIncentiveValue} rules={rules}>
              {redeemType === RedeemType.Points ? (
                <CurrencyInput
                  size={size}
                  currency={currency?.code}
                  placeholder="Reward Amount"
                  className={styles.multiplier}
                  disabled={!currentPlan.metadata.isEditing}
                />
              ) : (
                <InputNumber
                  min={1}
                  precision={0}
                  size={size}
                  className={styles.multiplier}
                  disabled={!currentPlan.metadata.isEditing}
                />
              )}
            </Item>
          </Col>
        </If>
      </Row>
      <If condition={isCustom}>
        <div>
          <div>
            Choose a specific date?&nbsp;
            <Switch
              defaultChecked={isSpecificDate}
              onChange={handleSwitchChange}
              disabled={!currentPlan.metadata.isEditing}
            />
          </div>
          <br />
          {isSpecificDate ? (
            <>
              <Item
                required
                name={names.customAggregationStartPeriod}
                label="Start date"
                rules={[{ required: true, message: 'Start date is required!' }]}
                initialValue={
                  initialValues &&
                  initialValues.customAggregationStartPeriod &&
                  !isCronValid(initialValues.customAggregationStartPeriod.toString())
                    ? dayjs(new Date(initialValues.customAggregationStartPeriod.toString()))
                    : undefined
                }
              >
                <DatePicker
                  format="MM-DD-YYYY HH:mm"
                  renderExtraFooter={() => null}
                  showTime
                  placeholder="Pick a date"
                  disabled={!currentPlan.metadata.isEditing}
                />
              </Item>
              <Item
                name={names.customAggregationEndPeriod}
                label="End date"
                initialValue={
                  initialValues &&
                  initialValues.customAggregationEndPeriod &&
                  dayjs(new Date(initialValues.customAggregationEndPeriod.toString())).isValid()
                    ? dayjs(new Date(initialValues.customAggregationEndPeriod.toString()))
                    : undefined
                }
              >
                <DatePicker
                  format="MM-DD-YYYY HH:mm"
                  renderExtraFooter={() => null}
                  showTime
                  placeholder="Pick a date"
                  disabled={!currentPlan.metadata.isEditing}
                />
              </Item>
            </>
          ) : (
            <>
              {names.customAggregationStartCronString && names.customAggregationEndCronString && (
                <>
                  <Item
                    required
                    name={names.customAggregationStartCronString}
                    label="Select aggregation period start:"
                    rules={[{ required: true, message: 'Start period is required!' }]}
                    initialValue={initialValues?.customAggregationStartCronString}
                  >
                    <CronPicker
                      value={form.getFieldValue(names.customAggregationStartCronString)}
                      setValue={(newValue: string) => {
                        if (names.customAggregationStartCronString) {
                          form.setFieldValue(names.customAggregationStartCronString, newValue);
                        }
                      }}
                      disabled={!currentPlan.metadata.isEditing}
                    />
                  </Item>
                  <Item
                    name={names.customAggregationEndCronString}
                    label="Select aggregation period end:"
                    initialValue={initialValues?.customAggregationEndCronString}
                  >
                    <CronPicker
                      value={form.getFieldValue(names.customAggregationEndCronString)}
                      setValue={(newValue: string) => {
                        if (names.customAggregationEndCronString) {
                          form.setFieldValue(names.customAggregationEndCronString, newValue);
                        }
                      }}
                      disabled={!currentPlan.metadata.isEditing}
                    />
                  </Item>
                </>
              )}
            </>
          )}
        </div>
      </If>
    </div>
  );
};

export default GenericPlanBuilderInput;
